import { OwlIcon } from '@/components/icons';
import { APP_ROUTER } from '@/routes/routes';
import {
  AppstoreOutlined,
  BankOutlined,
  BellOutlined,
  BookOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  FlagOutlined,
  FontColorsOutlined,
  FormOutlined,
  LineChartOutlined,
  MessageOutlined,
  MobileOutlined,
  NotificationOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  ProfileOutlined,
  QuestionCircleOutlined,
  QuestionOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  SnippetsOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UsergroupAddOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { IGetMeResponse } from '@/types/response';
import {
  ADMIN_ONLY,
  DASHBOARD_READERS,
  PermissionKey,
  hasAnyPermission,
} from '@/utils/permission';

/** permissions 省略時は管理者専用（routes の privateRoutes と同じ規則） */
export type NavLeaf = {
  key: string;
  label: string;
  icon?: React.ReactNode;
  permissions?: PermissionKey[];
};
/** children を持たないものはグループではなくメニュー直下のページ（key がパス） */
export type NavGroup = {
  key: string;
  label: string;
  icon: React.ReactNode;
  children?: NavLeaf[];
  permissions?: PermissionKey[];
};

/** グループならその子、直下のページならそれ自身 */
const leavesOf = (g: NavGroup): NavLeaf[] => g.children ?? [g];

const matchesPath = (key: string, pathname: string) =>
  pathname === key || pathname.startsWith(`${key}/`);

export const navGroups: NavGroup[] = [
  {
    key: APP_ROUTER.DASHBOARD,
    label: 'ダッシュボード',
    icon: <DashboardOutlined />,
    permissions: DASHBOARD_READERS,
  },
  {
    key: APP_ROUTER.KPI_INITIATIVES,
    label: '施策タイムライン',
    icon: <FlagOutlined />,
    permissions: DASHBOARD_READERS,
  },
  {
    key: APP_ROUTER.APP_USAGE,
    label: 'アプリ利用状況',
    icon: <MobileOutlined />,
    permissions: DASHBOARD_READERS,
  },
  {
    key: APP_ROUTER.COMPANY_REPORT,
    label: '企業別ダッシュボード',
    icon: <BankOutlined />,
    permissions: DASHBOARD_READERS,
  },
  {
    key: 'users',
    label: 'ユーザー',
    icon: <TeamOutlined />,
    children: [
      { key: APP_ROUTER.USER.LIST, label: 'ユーザー', icon: <UserOutlined /> },
      { key: APP_ROUTER.FAMILY.LIST, label: '家族一覧', icon: <TeamOutlined /> },
      { key: APP_ROUTER.POST.LIST, label: '投稿', icon: <ProfileOutlined /> },
    ],
  },
  {
    key: 'contents',
    label: 'コンテンツ',
    icon: <FileTextOutlined />,
    children: [
      { key: APP_ROUTER.CONTENT.LIST, label: 'コンテンツ', icon: <FileTextOutlined /> },
      { key: APP_ROUTER.PROMPT.LIST, label: 'プロンプト', icon: <FontColorsOutlined /> },
      { key: APP_ROUTER.ILLUSTRATION_THEME.LIST, label: 'イラストテーマ', icon: <PictureOutlined /> },
      { key: APP_ROUTER.COGEVO_STAMP_IMAGE.LIST, label: 'CogEvoスタンプ画像', icon: <PictureOutlined /> },
      { key: APP_ROUTER.SCREEN_MEDIA.LIST, label: '画面メディア', icon: <PlayCircleOutlined /> },
      { key: APP_ROUTER.SPECIAL_DAY.LIST, label: '今日は何の日', icon: <ProfileOutlined /> },
    ],
  },
  {
    key: 'broadcast',
    label: '配信',
    icon: <NotificationOutlined />,
    children: [
      { key: APP_ROUTER.NOTIFICATION.LIST, label: 'お知らせ', icon: <NotificationOutlined /> },
      { key: APP_ROUTER.NOTIFICATION_REMIND.LIST, label: 'プッシュ通知', icon: <BellOutlined /> },
      { key: APP_ROUTER.CALENDAR_POST.LIST, label: 'カレンダー投稿', icon: <SnippetsOutlined /> },
      { key: APP_ROUTER.BANNER.LIST, label: 'トップバナー', icon: <PictureOutlined /> },
    ],
  },
  {
    key: 'surveys',
    label: 'アンケート',
    icon: <FormOutlined />,
    children: [
      { key: APP_ROUTER.SURVEY.LIST, label: 'アンケート', icon: <FormOutlined /> },
      { key: APP_ROUTER.SURVEY_HEALTH.LIST, label: 'お口アンケート', icon: <QuestionCircleOutlined /> },
    ],
  },
  {
    key: 'fukumaru',
    label: 'ふくまる',
    icon: <OwlIcon />,
    children: [
      { key: APP_ROUTER.POST_FUKUMARU.LIST, label: 'ふくまる投稿', icon: <OwlIcon /> },
      { key: APP_ROUTER.CHAT_FUKUMARU.LIST, label: 'トーク', icon: <MessageOutlined /> },
      { key: APP_ROUTER.FUKUMARU_QUIZ.LIST, label: 'クイズ', icon: <QuestionCircleOutlined /> },
    ],
  },
  {
    key: 'preparation',
    label: '備え',
    icon: <DatabaseOutlined />,
    children: [
      { key: APP_ROUTER.PARTNER.LIST, label: '企業プロフィール', icon: <UsergroupAddOutlined /> },
      { key: APP_ROUTER.OFFER.LIST, label: 'オファー', icon: <FileTextOutlined /> },
      { key: APP_ROUTER.ARTICLE.LIST, label: '記事', icon: <FileTextOutlined /> },
      { key: APP_ROUTER.CATEGORY.LIST, label: 'カテゴリ', icon: <BookOutlined /> },
      { key: APP_ROUTER.EVENT.LIST, label: 'イベント', icon: <CalendarOutlined /> },
      { key: APP_ROUTER.AYUMI.LIST, label: 'あゆみ', icon: <CheckSquareOutlined /> },
      { key: APP_ROUTER.MENU.INQUIRY, label: 'お問い合わせ', icon: <QuestionOutlined /> },
      { key: APP_ROUTER.COMPANY.LIST, label: '団体情報', icon: <BankOutlined /> },
    ],
  },
  {
    key: 'kazokugoto',
    label: 'かぞくごと。',
    icon: <BookOutlined />,
    children: [
      // 長い path を先に置く（findLeafKeyForPath の startsWith マッチ衝突回避）
      { key: APP_ROUTER.KAZOKUGOTO.PROMPTS, label: 'プロンプト', icon: <FontColorsOutlined /> },
      { key: APP_ROUTER.KAZOKUGOTO.CHARACTERS, label: 'キャラクター', icon: <TeamOutlined /> },
      { key: APP_ROUTER.KAZOKUGOTO.TOPICS, label: '365日マスタ', icon: <SnippetsOutlined /> },
      { key: APP_ROUTER.KAZOKUGOTO.ITEMS, label: 'アイテム一覧', icon: <ProfileOutlined /> },
      { key: APP_ROUTER.KAZOKUGOTO.GENERATE, label: '生成', icon: <CalendarOutlined /> },
    ],
  },
  {
    key: 'agent',
    label: 'AIエージェント',
    icon: <RobotOutlined />,
    children: [
      // 長い path を先に置く（findLeafKeyForPath の startsWith マッチ衝突回避）
      { key: APP_ROUTER.AGENT.MESSAGES, label: '生成一覧', icon: <ProfileOutlined /> },
      { key: APP_ROUTER.AGENT.PROMPTS, label: 'プロンプト', icon: <FontColorsOutlined /> },
      { key: APP_ROUTER.AGENT.FLOW, label: 'フロー解説', icon: <BookOutlined /> },
      {
        key: APP_ROUTER.AGENT.HENKA,
        label: '変化のたより',
        icon: <LineChartOutlined />,
      },
      { key: APP_ROUTER.AGENT.RUN, label: '手動実行', icon: <ThunderboltOutlined /> },
    ],
  },
  {
    key: 'system',
    label: 'システム',
    icon: <SettingOutlined />,
    children: [
      { key: APP_ROUTER.VERSION.LIST, label: 'バージョン', icon: <AppstoreOutlined /> },
      {
        key: APP_ROUTER.ADMIN_USER.LIST,
        label: '管理ユーザー',
        icon: <SafetyCertificateOutlined />,
      },
    ],
  },
];

/** 権限の無いメニューを外す。中身が空になったグループごと消す */
export const filterNavGroups = (
  groups: NavGroup[],
  user: IGetMeResponse | null,
): NavGroup[] =>
  groups
    .map((g) =>
      g.children
        ? {
            ...g,
            children: g.children.filter((c) =>
              hasAnyPermission(user, c.permissions ?? ADMIN_ONLY),
            ),
          }
        : g,
    )
    .filter((g) =>
      g.children
        ? g.children.length > 0
        : hasAnyPermission(user, g.permissions ?? ADMIN_ONLY),
    );

export const toAntdMenuItems = (
  groups: NavGroup[],
  onTitleClick?: (key: string) => void,
): MenuProps['items'] =>
  groups.map((g) =>
    g.children
      ? {
          key: g.key,
          icon: g.icon,
          label: g.label,
          onTitleClick: onTitleClick ? () => onTitleClick(g.key) : undefined,
          children: g.children.map((c) => ({ key: c.key, icon: c.icon, label: c.label })),
        }
      : { key: g.key, icon: g.icon, label: g.label },
  );

/** 開くべきサブメニュー。直下のページなら開くものは無い */
export const findGroupKeyForPath = (groups: NavGroup[], pathname: string): string | undefined => {
  const match = groups.find((g) => g.children?.some((c) => matchesPath(c.key, pathname)));
  return match?.key;
};

export const findLeafKeyForPath = (groups: NavGroup[], pathname: string): string | undefined => {
  for (const g of groups) {
    const c = leavesOf(g).find((c) => matchesPath(c.key, pathname));
    if (c) return c.key;
  }
  return undefined;
};

export const findLeafLabelForPath = (groups: NavGroup[], pathname: string): { group?: string; leaf?: string } => {
  for (const g of groups) {
    const c = leavesOf(g).find((c) => matchesPath(c.key, pathname));
    if (c) return g.children ? { group: g.label, leaf: c.label } : { leaf: c.label };
  }
  return {};
};
